import { OrganizationStatus } from "@models/organization";
import { asApiFailure } from "@utils/apiFailure";

// Approve is FORBIDDEN for a Suspended or Cancelled org - you cannot onboard or
// bill a new location for an org you have suspended/cancelled. (There is no
// org-level Rejected state anymore, §1.2.) The server enforces this with a 409;
// the UI hides the control so an admin never fires a request guaranteed to fail.
export const canApproveLocations = (status: OrganizationStatus): boolean =>
    status !== OrganizationStatus.Suspended &&
    status !== OrganizationStatus.Cancelled;

export const APPROVE_BLOCKED_MESSAGE: Partial<
    Record<OrganizationStatus, string>
> = {
    [OrganizationStatus.Suspended]:
        "Approval is unavailable because this client is Suspended. Locations cannot be approved or billed for a suspended client.",
    [OrganizationStatus.Cancelled]:
        "Approval is unavailable because this client is Cancelled. Locations cannot be approved or billed for a cancelled client.",
};

// Substring of the server's no-pending-locations 409. The sibling 409 (org
// status forbids approval) carries different text, and the status code alone
// cannot tell them apart - hence ApiError.detail.
const NO_PENDING_LOCATIONS = "no pending locations";

const GENERIC_FAILURE =
    "Something went wrong while approving. Please try again.";

// Maps a failed approve into admin-facing copy. The two 409s MUST read
// differently: one means "nothing left to do", the other "this org may not be
// approved at all".
export const approveErrorMessage = (error: unknown): string => {
    const failure = asApiFailure(error);

    if (!failure) {
        return GENERIC_FAILURE;
    }

    switch (failure.status) {
        case 403:
            return "You do not have permission to approve clients.";

        case 404:
            return "This client no longer exists. It may have been deleted.";

        case 409:
            return failure.detail?.includes(NO_PENDING_LOCATIONS)
                ? "There are no pending locations to approve. Every location on this client has already been reviewed."
                : "This client's status no longer allows approval — it may have been rejected or suspended in another session. Refresh to see its current status.";

        default:
            return GENERIC_FAILURE;
    }
};
